import { html, nothing } from "lit";
import { icons } from "./icons.ts";

/**
 * BIBLE §3 — *Blokady, błędy i pytania wymagające decyzji użytkownika są
 * widoczne bez otwierania aktywności agentów. Dostępne jako lista
 * notyfikacji w WebUX.*
 *
 * The strip above the composer (chat-status-strip.ts) covers the "visible
 * without opening the agent activity" half of the rule. This component
 * covers the "available as a notification list in WebUX" half: a topbar
 * bell that lists every blocker / error / decision request across the
 * fleet, with a count badge, so the operator can triage from anywhere in
 * the app — not only from inside a chat.
 */

export type WebuxNotification = {
  id: string;
  tone: "blocked" | "error" | "warning" | "info";
  title: string;
  detail?: string | null;
  /** Session the notification belongs to, when known. */
  sessionKey?: string | null;
  /** Deep link into the chat where the item lives. */
  href?: string | null;
};

const TONE_LABEL: Record<WebuxNotification["tone"], string> = {
  blocked: "Needs decision",
  error: "Error",
  warning: "Attention",
  info: "Status",
};

/* -- Outside-click close ---------------------------------------------------
 * A document-level `mousedown` listener is attached only while the panel
 * is open, so the app carries no permanent global listener. */
let webuxNotificationsOutsideClickAttached = false;
let webuxNotificationsOutsideClickOnClose: (() => void) | null = null;

function webuxNotificationsDocMousedown(event: MouseEvent) {
  const target = event.target;
  if (target instanceof Element && target.closest?.(".webux-notifications")) {
    return;
  }
  unregisterWebuxNotificationsOutsideClick();
  webuxNotificationsOutsideClickOnClose?.();
}

export function registerWebuxNotificationsOutsideClick(onClose: () => void): void {
  if (typeof document === "undefined") return;
  webuxNotificationsOutsideClickOnClose = onClose;
  if (webuxNotificationsOutsideClickAttached) return;
  document.addEventListener("mousedown", webuxNotificationsDocMousedown);
  webuxNotificationsOutsideClickAttached = true;
}

export function unregisterWebuxNotificationsOutsideClick(): void {
  if (typeof document === "undefined") return;
  if (!webuxNotificationsOutsideClickAttached) return;
  document.removeEventListener("mousedown", webuxNotificationsDocMousedown);
  webuxNotificationsOutsideClickAttached = false;
  webuxNotificationsOutsideClickOnClose = null;
}

export type WebuxNotificationListProps = {
  notifications: WebuxNotification[];
  onSelect?: (notification: WebuxNotification) => void;
  onDismiss?: (id: string) => void;
};

/**
 * Collect the blockers / errors / decision requests that are visible in
 * the app right now, across every session, into a flat notification list.
 *
 * Sources:
 *  - `fleetQuestionQueue` — decision requests awaiting the operator.
 *  - `fleetTodosBySession` — per-session work items; a `cancelled` item
 *    with a `failureReason` is an error, a `pending` item whose content
 *    reads as blocked is a warning.
 */
export function collectWebuxNotifications(state: {
  fleetQuestionQueue?: ReadonlyArray<{
    id: string;
    sessionKey: string;
    title: string;
    answered?: unknown;
  }>;
  fleetTodosBySession?: Record<
    string,
    ReadonlyArray<{
      id: string;
      content: string;
      status: string;
      failureReason?: string;
      validationNotes?: string[];
    }>
  >;
}): WebuxNotification[] {
  const out: WebuxNotification[] = [];

  for (const question of state.fleetQuestionQueue ?? []) {
    if (question.answered) continue;
    out.push({
      id: `question:${question.id}`,
      tone: "blocked",
      title: question.title?.trim() || "The agent is waiting for your decision.",
      detail: question.sessionKey,
      sessionKey: question.sessionKey,
    });
  }

  for (const [sessionKey, todos] of Object.entries(state.fleetTodosBySession ?? {})) {
    for (const todo of todos ?? []) {
      const failure = todo.failureReason?.trim();
      if (todo.status === "cancelled" && failure) {
        out.push({
          id: `todo:${sessionKey}:${todo.id}`,
          tone: "error",
          title: todo.content?.trim() || "A task failed.",
          detail: failure,
          sessionKey,
        });
        continue;
      }
      if (todo.status === "pending" && /blocked|waiting for|needs decision/i.test(todo.content ?? "")) {
        out.push({
          id: `todo:${sessionKey}:${todo.id}`,
          tone: "warning",
          title: todo.content?.trim(),
          detail: todo.validationNotes?.join(" · ")?.trim() || null,
          sessionKey,
        });
      }
    }
  }

  return out;
}

export function renderWebuxNotificationList(props: WebuxNotificationListProps) {
  const items = props.notifications.filter((n) => n.title.trim().length > 0);
  const blocking = items.filter((n) => n.tone === "blocked" || n.tone === "error").length;

  return html`
    <div class="webux-notifications" data-testid="webux-notifications">
      <button
        type="button"
        class="webux-notifications__trigger"
        title=${blocking > 0 ? `${blocking} item${blocking === 1 ? "" : "s"} need attention` : "Notifications"}
        aria-label=${blocking > 0 ? `Notifications, ${blocking} need attention` : "Notifications"}
        aria-haspopup="dialog"
        aria-expanded="false"
        data-testid="webux-notifications-trigger"
        @click=${(e: Event) => {
          const host = (e.currentTarget as HTMLElement).closest(".webux-notifications");
          const panel = host?.querySelector<HTMLElement>(".webux-notifications__panel");
          const trigger = host?.querySelector<HTMLElement>(".webux-notifications__trigger");
          if (!panel || !trigger) return;
          const open = panel.hasAttribute("hidden");
          if (open) {
            panel.removeAttribute("hidden");
            trigger.setAttribute("aria-expanded", "true");
            registerWebuxNotificationsOutsideClick(() => {
              panel.setAttribute("hidden", "");
              trigger.setAttribute("aria-expanded", "false");
            });
          } else {
            panel.setAttribute("hidden", "");
            trigger.setAttribute("aria-expanded", "false");
            unregisterWebuxNotificationsOutsideClick();
          }
        }}
      >
        <span class="webux-notifications__icon" aria-hidden="true">${icons.bell}</span>
        ${items.length > 0
          ? html`<span class="webux-notifications__badge" data-testid="webux-notifications-badge"
              >${items.length}</span
            >`
          : nothing}
      </button>
      <div
        class="webux-notifications__panel"
        role="dialog"
        aria-label="Notifications"
        hidden
        data-testid="webux-notifications-panel"
      >
        <header class="webux-notifications__head">
          <span class="webux-notifications__head-title">Notifications</span>
          <span class="webux-notifications__head-count">${items.length}</span>
        </header>
        ${items.length === 0
          ? html`
              <p class="webux-notifications__empty">Nothing needs your attention.</p>
            `
          : html`
              <ul class="webux-notifications__list">
                ${items.map(
                  (item) => html`
                    <li
                      class="webux-notifications__item webux-notifications__item--${item.tone}"
                      data-testid="webux-notification-item"
                    >
                      <button
                        type="button"
                        class="webux-notifications__item-main"
                        @click=${() => props.onSelect?.(item)}
                      >
                        <span class="webux-notifications__item-tone">${TONE_LABEL[item.tone]}</span>
                        <span class="webux-notifications__item-title">${item.title}</span>
                        ${item.detail?.trim()
                          ? html`<span class="webux-notifications__item-detail">${item.detail}</span>`
                          : nothing}
                        ${item.sessionKey
                          ? html`<span class="webux-notifications__item-session mono"
                              >${item.sessionKey}</span
                            >`
                          : nothing}
                      </button>
                      ${props.onDismiss
                        ? html`
                            <button
                              type="button"
                              class="webux-notifications__item-dismiss"
                              aria-label="Dismiss ${item.title}"
                              title="Dismiss"
                              @click=${() => props.onDismiss?.(item.id)}
                            >
                              ${icons.x}
                            </button>
                          `
                        : nothing}
                    </li>
                  `,
                )}
              </ul>
            `}
      </div>
    </div>
  `;
}
